// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file BCN.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Integração BCN — Direct Transfer API (Sandbox)
// @date 2026-05-15
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  Add,
  Wifi,
  WifiOff,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, CurrencyCell, DateCell } from '../../components/Common/DataTable';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BcnTransfer {
  id: string;
  bcn_order_id: string | null;
  pagali_ref: string;
  direction: 'INBOUND' | 'OUTBOUND';
  amount_minor: number;
  currency: string;
  creditor_iban: string;
  creditor_name: string;
  description: string | null;
  status: string;
  initiated_by: string | null;
  created_at: string;
  updated_at: string;
}

interface NewTransferForm {
  creditorIban: string;
  creditorName: string;
  amountCve: string;
  description: string;
  pagaliRef: string;
}

// ─── Status colour map ────────────────────────────────────────────────────────

const BCN_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  executed: 'success',
  approved: 'info',
  sent: 'info',
  processing: 'info',
  pending_approval: 'warning',
  pending: 'warning',
  pending_retry: 'warning',
  rejected: 'error',
  returned: 'error',
  failed: 'error',
};

const BCN_STATUS_LABEL: Record<string, string> = {
  pending_approval: 'Aguarda aprovação',
  approved: 'Aprovada',
  sent: 'Enviada',
  processing: 'A processar',
  executed: 'Executada',
  pending: 'Suspensa',
  pending_retry: 'A retry',
  rejected: 'Rejeitada',
  returned: 'Revertida',
  failed: 'Erro',
};

// ─── Table columns ────────────────────────────────────────────────────────────

const columns: Column<BcnTransfer>[] = [
  {
    id: 'status',
    label: 'Estado',
    minWidth: 160,
    format: (_v, row) => (
      <StatusChip
        status={BCN_STATUS_LABEL[row.status] ?? row.status}
        colorMap={{ [BCN_STATUS_LABEL[row.status] ?? row.status]: BCN_STATUS_COLOR[row.status] ?? 'default' }}
      />
    ),
  },
  {
    id: 'direction',
    label: 'Tipo',
    minWidth: 110,
    format: (_v, row) => (
      <Chip
        size="small"
        label={row.direction}
        color={row.direction === 'OUTBOUND' ? 'primary' : 'secondary'}
        variant="outlined"
      />
    ),
  },
  {
    id: 'creditor_name',
    label: 'Beneficiário',
    format: (_v, row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.creditor_name}</Typography>
        <Typography variant="caption" color="text.secondary">{row.creditor_iban}</Typography>
      </Box>
    ),
  },
  {
    id: 'amount_minor',
    label: 'Montante',
    align: 'right',
    minWidth: 130,
    format: (_v, row) => <CurrencyCell value={row.amount_minor} currency={row.currency} />,
  },
  {
    id: 'pagali_ref',
    label: 'Referência Pagali',
    format: (_v, row) => (
      <Typography variant="caption" fontFamily="monospace">{row.pagali_ref}</Typography>
    ),
  },
  {
    id: 'bcn_order_id',
    label: 'ID BCN',
    format: (_v, row) => (
      <Typography variant="caption" fontFamily="monospace" color="text.secondary">
        {row.bcn_order_id ?? '—'}
      </Typography>
    ),
  },
  {
    id: 'created_at',
    label: 'Data',
    minWidth: 130,
    format: (_v, row) => <DateCell value={row.created_at} includeTime />,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const BankingBCN: React.FC = () => {
  const [transfers, setTransfers] = useState<BcnTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'online' | 'offline' | 'checking'>('unknown');
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const [newTransferOpen, setNewTransferOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState<NewTransferForm>({
    creditorIban: '',
    creditorName: '',
    amountCve: '',
    description: '',
    pagaliRef: `PAGALI-${Date.now()}`,
  });

  const checkConnection = useCallback(async () => {
    setConnectionStatus('checking');
    setConnectionError(null);
    try {
      const res = await api.get('/client/banking/bcn/health');
      setConnectionStatus(res.data.status === 'online' ? 'online' : 'offline');
      if (res.data.status !== 'online') {
        setConnectionError(`BCN status: ${res.data.bcn_status}`);
      }
    } catch (err: any) {
      setConnectionStatus('offline');
      setConnectionError(err.response?.data?.error ?? err.message);
    }
  }, []);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
      });
      const res = await api.get(`/client/banking/bcn/transfers?${params}`);
      setTransfers(res.data.data ?? []);
      setTotalCount(res.data.pagination?.total ?? 0);
    } catch (err: any) {
      console.error('BCN transfers fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    checkConnection();
    fetchTransfers();
  }, [checkConnection, fetchTransfers]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post('/client/banking/bcn/transfers', {
        ...form,
        amountCve: Number(form.amountCve),
      });
      setNewTransferOpen(false);
      setForm({ creditorIban: '', creditorName: '', amountCve: '', description: '', pagaliRef: `PAGALI-${Date.now()}` });
      fetchTransfers();
    } catch (err: any) {
      setSubmitError(err.response?.data?.error ?? 'Erro ao criar transferência');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AccountBalance color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>BCN — Direct Transfer</Typography>
            <Typography variant="body2" color="text.secondary">
              Banco Comercial do Atlântico · Sandbox
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Testar ligação">
            <IconButton onClick={checkConnection} disabled={connectionStatus === 'checking'}>
              {connectionStatus === 'checking' ? (
                <CircularProgress size={20} />
              ) : connectionStatus === 'online' ? (
                <Wifi color="success" />
              ) : connectionStatus === 'offline' ? (
                <WifiOff color="error" />
              ) : (
                <Wifi color="action" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchTransfers} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewTransferOpen(true)}
            disabled={connectionStatus !== 'online'}
          >
            Nova Transferência
          </Button>
        </Box>
      </Box>

      {/* Connection status */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ py: '12px !important', display: 'flex', alignItems: 'center', gap: 2 }}>
          {connectionStatus === 'online' ? (
            <CheckCircle color="success" />
          ) : connectionStatus === 'offline' ? (
            <ErrorIcon color="error" />
          ) : (
            <CircularProgress size={20} />
          )}
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {connectionStatus === 'online'
                ? 'Ligado ao BCN Sandbox'
                : connectionStatus === 'offline'
                ? 'Sem ligação ao BCN'
                : 'A verificar ligação...'}
            </Typography>
            {connectionError && (
              <Typography variant="caption" color="error">{connectionError}</Typography>
            )}
            {connectionStatus === 'unknown' && (
              <Typography variant="caption" color="text.secondary">
                Clique no ícone Wi-Fi para testar
              </Typography>
            )}
          </Box>
          {connectionStatus === 'offline' && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Verifica BCN_CLIENT_ID e BCN_CLIENT_SECRET no .env.pagali-sandbox
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Transfers table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Transferências BCN
          </Typography>
          <DataTable
            columns={columns}
            data={transfers}
            loading={loading}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            emptyMessage="Ainda não há transferências BCN registadas"
          />
        </CardContent>
      </Card>

      {/* New transfer dialog */}
      <Dialog open={newTransferOpen} onClose={() => setNewTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Transferência BCN</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <TextField
              label="IBAN do Beneficiário"
              value={form.creditorIban}
              onChange={(e) => setForm((f) => ({ ...f, creditorIban: e.target.value }))}
              placeholder="CV05 0000 0000 0000 0000 0000 0"
              fullWidth
              required
            />
            <TextField
              label="Nome do Beneficiário"
              value={form.creditorName}
              onChange={(e) => setForm((f) => ({ ...f, creditorName: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Montante (CVE)"
              value={form.amountCve}
              onChange={(e) => setForm((f) => ({ ...f, amountCve: e.target.value }))}
              type="number"
              InputProps={{ endAdornment: <InputAdornment position="end">CVE</InputAdornment> }}
              fullWidth
              required
            />
            <Divider />
            <TextField
              label="Descrição / Motivo"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Referência Pagali"
              value={form.pagaliRef}
              onChange={(e) => setForm((f) => ({ ...f, pagaliRef: e.target.value }))}
              fullWidth
              required
              helperText="Identificador único desta operação no Pagali"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTransferOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !form.creditorIban || !form.creditorName || !form.amountCve || !form.pagaliRef}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            Criar Transferência
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankingBCN;
